import { create } from 'zustand'
import { api } from '@/lib/api'

interface UserListItem {
  id: string
  email: string
  name: string
  emailVerified: boolean | null
  firstName: string | null
  lastName: string | null
  avatarUrl: string | null
  role: string | null
  banned: boolean | null
  banReason: string | null
  twoFactorEnabled: boolean | null
  lastActiveAt: string | null
  createdAt: string | null
  availableCredits: number | null
  totalCreditsUsed: number | null
  providerId: string | null
  accountId: string | null
}

interface UserDetail {
  id: string
  email: string
  name: string
  emailVerified: boolean | null
  firstName: string | null
  lastName: string | null
  avatarUrl: string | null
  nickname: string | null
  bio: string | null
  website: string | null
  location: string | null
  phone: string | null
  role: string | null
  banned: boolean | null
  banReason: string | null
  banExpires: string | null
  twoFactorEnabled: boolean | null
  lastActiveAt: string | null
  createdAt: string | null
  availableCredits: number | null
  totalCreditsEarned: number | null
  totalCreditsUsed: number | null
  sessions: SessionEntry[]
}

interface SessionEntry {
  id: string
  ipAddress: string | null
  userAgent: string | null
  createdAt: string | null
  expiresAt: string
}

interface ActivityEntry {
  type: 'credit_usage' | 'tryon_request'
  timestamp: string
  data: Record<string, unknown>
}

interface PurchaseEntry {
  id: string
  transactionId: string
  originalTransactionId: string
  productId: string
  productLabel: string
  creditsGranted: number
  purchaseDate: string
  environment: string
  verifiedWithApple: boolean
  refundedAt: string | null
  refundReason: string | null
  refundedBy: string | null
  createdAt: string
}

interface Pagination {
  page: number
  limit: number
  total: number
  totalPages: number
}

type SortField = 'name' | 'email' | 'role' | 'availableCredits' | 'lastActiveAt' | 'createdAt'
type SortDirection = 'asc' | 'desc'

interface CreateUserPayload {
  email: string
  name?: string
  firstName?: string
  lastName?: string
  role?: 'admin' | 'user'
  emailVerified?: boolean
}

interface CreateUserResult {
  user: {
    id: string
    email: string
    name: string
    role: string | null
    emailVerified: boolean | null
  }
  temporaryPassword: string
}

interface UsersState {
  users: UserListItem[]
  pagination: Pagination
  searchQuery: string
  roleFilter: string
  emailVerificationFilter: string
  bannedFilter: string
  sortField: SortField
  sortDirection: SortDirection
  isLoading: boolean
  error: string | null

  selectedUser: UserDetail | null
  isLoadingDetail: boolean

  activity: ActivityEntry[]
  activityPagination: Pagination
  isLoadingActivity: boolean

  userSessions: SessionEntry[]
  sessionsPagination: Pagination
  isLoadingSessions: boolean

  purchases: PurchaseEntry[]
  purchasesPagination: Pagination
  isLoadingPurchases: boolean

  setSearchQuery: (query: string) => void
  setRoleFilter: (role: string) => void
  setEmailVerificationFilter: (emailVerified: string) => void
  setBannedFilter: (banned: string) => void
  setSort: (field: SortField, direction?: SortDirection) => void

  fetchUsers: (page?: number) => Promise<void>
  fetchUserDetail: (id: string) => Promise<void>
  fetchActivity: (id: string, page?: number) => Promise<void>
  fetchSessions: (id: string, page?: number) => Promise<void>
  createUser: (payload: CreateUserPayload) => Promise<CreateUserResult>
  adjustCredits: (id: string, amount: number, reason: string) => Promise<void>
  verifyEmail: (id: string) => Promise<void>
  unverifyEmail: (id: string) => Promise<void>
  banUser: (id: string, reason?: string) => Promise<void>
  unbanUser: (id: string) => Promise<void>
  revokeSession: (userId: string, sessionId: string) => Promise<void>
  fetchPurchases: (id: string, page?: number) => Promise<void>
  refundPurchase: (userId: string, purchaseId: string, reason: string) => Promise<void>
  clearDetail: () => void
}

export const useUsersStore = create<UsersState>((set, get) => ({
  users: [],
  pagination: { page: 1, limit: 25, total: 0, totalPages: 0 },
  searchQuery: '',
  roleFilter: '',
  emailVerificationFilter: '',
  bannedFilter: '',
  sortField: 'createdAt',
  sortDirection: 'desc',
  isLoading: false,
  error: null,

  selectedUser: null,
  isLoadingDetail: false,

  activity: [],
  activityPagination: { page: 1, limit: 25, total: 0, totalPages: 0 },
  isLoadingActivity: false,

  userSessions: [],
  sessionsPagination: { page: 1, limit: 25, total: 0, totalPages: 0 },
  isLoadingSessions: false,

  purchases: [],
  purchasesPagination: { page: 1, limit: 25, total: 0, totalPages: 0 },
  isLoadingPurchases: false,

  setSearchQuery: (searchQuery) => {
    set({ searchQuery })
    get().fetchUsers(1)
  },

  setRoleFilter: (roleFilter) => {
    set({ roleFilter })
    get().fetchUsers(1)
  },

  setEmailVerificationFilter: (emailVerificationFilter) => {
    set({ emailVerificationFilter })
    get().fetchUsers(1)
  },

  setBannedFilter: (bannedFilter) => {
    set({ bannedFilter })
    get().fetchUsers(1)
  },

  setSort: (field, direction) => {
    const { sortField, sortDirection } = get()
    const newDirection =
      direction ?? (sortField === field && sortDirection === 'desc' ? 'asc' : 'desc')
    set({ sortField: field, sortDirection: newDirection })
    get().fetchUsers(1)
  },

  fetchUsers: async (page = 1) => {
    set({ isLoading: true, error: null })
    const {
      searchQuery,
      roleFilter,
      emailVerificationFilter,
      bannedFilter,
      sortField,
      sortDirection,
    } = get()
    try {
      const res = await api.get<{
        data: { users: UserListItem[]; pagination: Pagination }
      }>('/api/v1/admin/users', {
        page,
        limit: 25,
        search: searchQuery || undefined,
        role: roleFilter || undefined,
        emailVerified: emailVerificationFilter || undefined,
        banned: bannedFilter || undefined,
        sort: sortField,
        direction: sortDirection,
      })
      set({ users: res.data.users, pagination: res.data.pagination, isLoading: false })
    } catch (err) {
      set({ error: (err as Error).message, isLoading: false })
    }
  },

  fetchUserDetail: async (id) => {
    set({ isLoadingDetail: true })
    try {
      const res = await api.get<{ data: UserDetail }>(`/api/v1/admin/users/${id}`)
      set({ selectedUser: res.data, isLoadingDetail: false })
    } catch (err) {
      set({ error: (err as Error).message, isLoadingDetail: false })
    }
  },

  fetchActivity: async (id, page = 1) => {
    set({ isLoadingActivity: true })
    try {
      const res = await api.get<{
        data: { entries: ActivityEntry[]; pagination: Pagination }
      }>(`/api/v1/admin/users/${id}/activity`, { page, limit: 25 })
      set({
        activity: res.data.entries,
        activityPagination: res.data.pagination,
        isLoadingActivity: false,
      })
    } catch (err) {
      console.error('Failed to fetch activity:', err)
      set({ isLoadingActivity: false })
    }
  },

  fetchSessions: async (id, page = 1) => {
    set({ isLoadingSessions: true })
    try {
      const res = await api.get<{
        data: { sessions: SessionEntry[]; pagination: Pagination }
      }>(`/api/v1/admin/users/${id}/sessions`, { page, limit: 25 })
      set({
        userSessions: res.data.sessions,
        sessionsPagination: res.data.pagination,
        isLoadingSessions: false,
      })
    } catch (err) {
      console.error('Failed to fetch sessions:', err)
      set({ isLoadingSessions: false })
    }
  },

  createUser: async (payload) => {
    const res = await api.post<{ data: CreateUserResult }>('/api/v1/admin/users', payload)
    await get().fetchUsers(1)
    return res.data
  },

  adjustCredits: async (id, amount, reason) => {
    await api.post(`/api/v1/admin/users/${id}/credits`, { amount, reason })
    await get().fetchUserDetail(id)
  },

  verifyEmail: async (id) => {
    await api.post(`/api/v1/admin/users/${id}/verify-email`)
    await get().fetchUsers(get().pagination.page)
    await get().fetchUserDetail(id)
  },

  unverifyEmail: async (id) => {
    await api.post(`/api/v1/admin/users/${id}/unverify-email`)
    await get().fetchUsers(get().pagination.page)
    await get().fetchUserDetail(id)
  },

  banUser: async (id, reason) => {
    await api.post(`/api/v1/admin/users/${id}/ban`, { reason })
    await get().fetchUsers(get().pagination.page)
    await get().fetchUserDetail(id)
  },

  unbanUser: async (id) => {
    await api.post(`/api/v1/admin/users/${id}/unban`)
    await get().fetchUsers(get().pagination.page)
    await get().fetchUserDetail(id)
  },

  revokeSession: async (userId, sessionId) => {
    await api.delete(`/api/v1/admin/users/${userId}/sessions/${sessionId}`)
    await get().fetchSessions(userId, get().sessionsPagination.page)
    await get().fetchUserDetail(userId)
  },

  fetchPurchases: async (id, page = 1) => {
    set({ isLoadingPurchases: true })
    try {
      const res = await api.get<{
        data: { purchases: PurchaseEntry[]; pagination: Pagination }
      }>(`/api/v1/admin/purchases/users/${id}/purchases`, { page, limit: 25 })
      set({
        purchases: res.data.purchases,
        purchasesPagination: res.data.pagination,
        isLoadingPurchases: false,
      })
    } catch (err) {
      console.error('Failed to fetch purchases:', err)
      set({ isLoadingPurchases: false })
    }
  },

  refundPurchase: async (userId, purchaseId, reason) => {
    await api.post(`/api/v1/admin/purchases/users/${userId}/purchases/${purchaseId}/refund`, {
      reason,
    })
    await get().fetchPurchases(userId, get().purchasesPagination.page)
    await get().fetchUserDetail(userId)
  },

  clearDetail: () => {
    set({
      selectedUser: null,
      activity: [],
      activityPagination: { page: 1, limit: 25, total: 0, totalPages: 0 },
      userSessions: [],
      sessionsPagination: { page: 1, limit: 25, total: 0, totalPages: 0 },
      purchases: [],
      purchasesPagination: { page: 1, limit: 25, total: 0, totalPages: 0 },
    })
  },
}))
